import { HttpClient, HttpContext, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseService } from '@/core/api/base-service';
import { ApiConfiguration } from '@/core/api/api-configuration';
import {
    AnotacaoInscricaoRequest,
    AnotacaoEleicoesRequest,
    AnotacaoDocumentosRequest,
    AnotacaoPageQuery,
    AnotacaoPortalRequest,
    AnotacaoRequest,
    AnotacaoRgmRequest,
    AnotacaoVistoProvisorioRequest,
    MedicoAnotacaoInsertRequest
} from '@/core/api/sicom-api/registro/medico/anotacao/anotacao.model';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { Page } from '@/shared/models/page.model';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

@Injectable({ providedIn: 'root' })
export class AnotacaoService extends BaseService {
    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }
    readonly PATH = SICOM_PATHS.registro.medico.anotacao;

    private pessoaParams(p: Pessoa, query?: AnotacaoPageQuery): HttpParams {
        let params = new HttpParams().set('id', String(p.id)).set('uf', String(p.uf));
        if (query?.page != null) params = params.set('page', String(query.page));
        if (query?.size != null) params = params.set('size', String(query.size));
        if (query?.direction != null) params = params.set('direction', query.direction);
        return params;
    }

    buscar(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoRequest>> {
        return this.http.get<Page<AnotacaoRequest>>(`${this.PATH}/consulta`, { params: this.pessoaParams(params, query), context });
    }

    buscarVistoProvisorio(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoVistoProvisorioRequest>> {
        return this.http.get<Page<AnotacaoVistoProvisorioRequest>>(`${this.PATH}/consulta-visto-provisorio`, { params: this.pessoaParams(params, query), context });
    }

    buscarRgm(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoRgmRequest>> {
        return this.http.get<Page<AnotacaoRgmRequest>>(`${this.PATH}/consulta-rgm`, { params: this.pessoaParams(params, query), context });
    }

    buscarPortal(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoPortalRequest>> {
        return this.http.get<Page<AnotacaoPortalRequest>>(`${this.PATH}/consulta-portal`, { params: this.pessoaParams(params, query), context });
    }

    buscarInscricao(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoInscricaoRequest>> {
        return this.http.get<Page<AnotacaoInscricaoRequest>>(`${this.PATH}/consulta-inscricao`, { params: this.pessoaParams(params, query), context });
    }

    buscarEleicoes(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoEleicoesRequest>> {
        return this.http.get<Page<AnotacaoEleicoesRequest>>(`${this.PATH}/consulta-eleicoes`, { params: this.pessoaParams(params, query), context });
    }

    buscarDocumentos(params: Pessoa, query?: AnotacaoPageQuery, context?: HttpContext): Observable<Page<AnotacaoDocumentosRequest>> {
        return this.http.get<Page<AnotacaoDocumentosRequest>>(`${this.PATH}/consulta-documentos`, { params: this.pessoaParams(params, query), context });
    }

    inserirAnotacao(pessoa: Pessoa, body: MedicoAnotacaoInsertRequest, context?: HttpContext): Observable<boolean> {
        return this.http.post(`${this.PATH}`, body, { params: this.pessoaParams(pessoa), observe: 'response', context }).pipe(map((r: HttpResponse<any>) => r.status === 201));
    }

    excluirAnotacao(pessoa: Pessoa, idHistoricoMedico: number, context?: HttpContext): Observable<boolean> {
        return this.http.delete(`${this.PATH}/${idHistoricoMedico}`, { params: this.pessoaParams(pessoa), observe: 'response', context }).pipe(
            map((r: HttpResponse<any>) => r.status === 204),
            catchError((e: HttpErrorResponse) => {
                if (e.status === 404) return of(false);
                return throwError(() => e);
            })
        );
    }
}
